import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { whatsappConnectionKeys } from './useWhatsAppConnections'
import { widgetKeys } from './useWidgets'
import type { WhatsAppSender } from './useWhatsAppConnections'

export interface WhatsAppOnboardingStatus {
  connected: boolean
  awaiting_otp: boolean
  sender_offline: boolean
  sender_added: boolean
  attached_to_messaging_service: boolean
  sender_online: boolean
  waba_id: string | null
  meta_phone_number_id: string | null
  twilio_subaccount_sid: string | null
  twilio_subaccount_name: string | null
  messaging_service_sid: string | null
  twilio_sender_sid: string | null
  whatsapp_status: string | null
  phone_number: string | null
  error: string | null
  can_programmatic_retry: boolean
}

export interface VerificationRedirectStatus {
  active: boolean
  redirect_target: string | null
  remaining_seconds: number
}

export interface WABASummary {
  waba_id: string
}

export const whatsappSignupKeys = {
  all: ['messaging-whatsapp-onboarding'] as const,
  status: (assistantId: number) => [...whatsappSignupKeys.all, 'status', assistantId] as const,
  redirect: (assistantId: number) => [...whatsappSignupKeys.all, 'redirect', assistantId] as const,
  wabas: () => [...whatsappSignupKeys.all, 'wabas'] as const,
}

function invalidateOnboardingState(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.all })
  void queryClient.invalidateQueries({ queryKey: whatsappConnectionKeys.all })
  // The widget hub derives its status from the database, so a connection change
  // has to invalidate it too or the hub keeps showing the old state.
  void queryClient.invalidateQueries({ queryKey: widgetKeys.all })
}

export function useCustomerWabas() {
  return useQuery({
    queryKey: whatsappSignupKeys.wabas(),
    queryFn: async (): Promise<WABASummary[]> => {
      const response = await api.get('/messaging/whatsapp/onboarding/wabas')
      return response.data
    },
    staleTime: 60 * 1000,
  })
}

export function useWhatsAppStatus(assistantId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: whatsappSignupKeys.status(assistantId),
    queryFn: async (): Promise<WhatsAppOnboardingStatus> => {
      const response = await api.get(`/messaging/whatsapp/onboarding/status/${assistantId}`)
      return response.data
    },
    enabled: options?.enabled ?? !!assistantId,
    staleTime: 10 * 1000,
    // Poll while onboarding is in-flight so the loading card shows live
    // progress and the connected view renders as soon as Meta finishes.
    refetchInterval: (query) => {
      const s = query.state.data
      if (!s) return false
      if (s.connected) return false
      if (s.sender_added || s.awaiting_otp || s.sender_offline) return 3000
      return false
    },
  })
}

export function useConnectWhatsAppNumber() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      assistant_id: number
      waba_id: string
      phone_number_id: string
    }): Promise<WhatsAppSender> => {
      const response = await api.post('/messaging/whatsapp/onboarding/connect', data)
      return response.data
    },
    onSuccess: () => invalidateOnboardingState(queryClient),
  })
}

export function useRedirectStatus(assistantId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: whatsappSignupKeys.redirect(assistantId),
    queryFn: async (): Promise<VerificationRedirectStatus> => {
      const response = await api.get(`/messaging/whatsapp/onboarding/redirect-status/${assistantId}`)
      return response.data
    },
    enabled: options?.enabled ?? !!assistantId,
    // Poll while the redirect is active so the countdown ticks; stop when inactive.
    refetchInterval: (query) => (query.state.data?.active ? 2000 : false),
  })
}

/**
 * Fire-and-forget funnel telemetry: forwards WA_EMBEDDED_SIGNUP browser
 * events (FINISH/CANCEL/ERROR) to the backend. Must never break the signup
 * flow, so errors are swallowed.
 */
export function reportSignupEvent(payload: {
  assistant_id: number
  event: string
  data: Record<string, unknown>
}): void {
  api.post('/messaging/whatsapp/onboarding/webhooks/app', payload).catch(() => { })
}

export function useActivateRedirect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { assistant_id: number; redirect_phone_number: string }): Promise<void> => {
      await api.post('/messaging/whatsapp/onboarding/activate-redirect', data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.redirect(variables.assistant_id) })
    },
  })
}

export function useDeactivateRedirect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { assistant_id: number }): Promise<void> => {
      await api.post('/messaging/whatsapp/onboarding/deactivate-redirect', data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.redirect(variables.assistant_id) })
    },
  })
}

export interface TwilioSenderOfflineReason {
  code: string
  message: string
  more_info: string
}

export interface TwilioSenderStatus {
  sender_sid: string
  status: string
  verification_method: string | null
  waba_id: string | null
  offline_reasons: TwilioSenderOfflineReason[]
  // Backend fills these when retry is pointless (e.g. Meta 63110 — number
  // claimed by another WABA). Frontend renders `remediation` in place of the
  // resend button when `blocked_reason` is set.
  blocked_reason: string | null
  remediation: string | null
}

export function useFetchSenderStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { assistant_id: number }): Promise<TwilioSenderStatus> => {
      const response = await api.get(`/messaging/whatsapp/onboarding/sender-status/${data.assistant_id}`)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.status(variables.assistant_id) })
    },
  })
}

export function useResendOtp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      assistant_id: number
      redirect_phone_number?: string
    }): Promise<TwilioSenderStatus> => {
      const response = await api.post('/messaging/whatsapp/onboarding/resend-otp', data)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.status(variables.assistant_id) })
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.redirect(variables.assistant_id) })
    },
  })
}

export function useVerifyNumber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { assistant_id: number; code: string }): Promise<WhatsAppSender> => {
      const response = await api.post('/messaging/whatsapp/onboarding/verify-number', data)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.status(variables.assistant_id) })
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.redirect(variables.assistant_id) })
    },
  })
}

export interface BusinessProfile {
  business_display_name: string | null
  about: string | null
  address: string | null
  description: string | null
  vertical: string | null
  logo_url: string | null
  email: string | null
  website: string | null
  additional_website: string | null
}

export type BusinessProfileUpdate = Omit<BusinessProfile, 'business_display_name'>

export function useBusinessProfile(assistantId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...whatsappSignupKeys.all, 'business-profile', assistantId] as const,
    queryFn: async (): Promise<BusinessProfile> => {
      const response = await api.get(
        `/messaging/whatsapp/onboarding/business-profile/${assistantId}`,
      )
      return response.data
    },
    enabled: options?.enabled ?? !!assistantId,
    staleTime: 60 * 1000,
  })
}

export function usePrefillBusinessProfile(assistantId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<BusinessProfile> => {
      const response = await api.post(
        `/messaging/whatsapp/onboarding/business-profile/${assistantId}/prefill`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...whatsappSignupKeys.all, 'business-profile', assistantId],
      })
    },
  })
}

export function useUpdateBusinessProfile(assistantId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<BusinessProfileUpdate>): Promise<BusinessProfile> => {
      const response = await api.put(
        `/messaging/whatsapp/onboarding/business-profile/${assistantId}`,
        payload,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...whatsappSignupKeys.all, 'business-profile', assistantId],
      })
    },
  })
}

export function useResetOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { assistant_id: number }): Promise<WhatsAppOnboardingStatus> => {
      const response = await api.post('/messaging/whatsapp/onboarding/reset', data)
      return response.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: whatsappSignupKeys.status(variables.assistant_id) })
      queryClient.invalidateQueries({ queryKey: whatsappConnectionKeys.all })
      queryClient.invalidateQueries({ queryKey: widgetKeys.all })
    },
  })
}
